import React, { useState, useEffect, useCallback } from 'react'
import { Card, makeStyles, Grid, Tab, Tabs, GoogleMapsComponent, useClientOptions } from '@paintscout/ui'
import type { MuiTheme as Theme } from '@paintscout/ui'
import DocumentTitle from 'src/components/common/DocumentTitle'
import BasePage from 'src/components/layouts/BasePage'
import LocationSearchBar from 'src/components/common/LocationSearchBar'
import { useSearchLocationsLazyQuery } from '@paintscout/api'
import { getObjectLabels } from '@paintscout/util/builder'
import { useHistory, useLocation } from 'react-router-dom'
import { jsonToLucene } from 'json-to-lucene'
import { useSnackbar } from 'notistack'
import { setUrlQuery, getUrlQuery } from 'shared/util/routing'
import type { ValueFilter } from 'json-to-lucene'
import NewLocationButton from 'src/components/common/NewLocationButton'
import { getFiltersFromLocation } from 'src/components/common/QuoteSearchBar'
import LocationSearchTable from '../../../common/LocationSearchTable'

const useStyles = makeStyles<Theme>(
  (theme) => ({
    root: {},
    tabs: {
      paddingLeft: 0,
      marginBottom: 0,
      [theme.breakpoints.down('md')]: {
        '& button': {
          marginRight: theme.spacing(2)
        }
      }
    }
  }),
  { name: 'Locations' }
)

export interface UrlQuery {
  search?: string
  page?: number
  filters: ValueFilter[]
}

export interface SearchProps {
  text: string
  filters: ValueFilter[]
  isInitialSearch?: boolean
}
export interface UrlParams {
  sort: string
  page: number
  search: string
  tab: 'all' | 'map'
}

function Locations() {
  const classes = useStyles()
  const { options } = useClientOptions()
  const objectLabels = getObjectLabels({ options })
  const location = useLocation<{ page: number }>()
  const history = useHistory()
  const urlParams = getUrlQuery<UrlParams>({ location })
  const sort = urlParams.sort || '-updated'
  const { enqueueSnackbar } = useSnackbar()
  const limit = 50
  const [page, setPage] = useState(location.state?.page ?? 1)
  const [filters, setFilters] = useState(getFiltersFromLocation(location, true))
  const [query, setQuery] = useState('') // will get populated from SearchBar
  const [showMapView, setShowMapView] = useState(false)
  const [tab, setTab] = useState(urlParams.tab ?? 'list')

  const [search, { loading, data, error, fetchMore }] = useSearchLocationsLazyQuery({
    variables: {
      query,
      limit
    },
    notifyOnNetworkStatusChange: true,
    onCompleted: (newData) => {
      // reset page back to 1 if user refreshes the page (cache is reset)
      if (
        page * limit > newData?.searchLocations?.rows.length &&
        newData?.searchLocations?.rows.length !== newData?.searchLocations?.total_rows
      ) {
        setPage(1)
      }
    }
  })
  function handleLocationSearch({ text, filters, isInitialSearch }: SearchProps) {
    let query = jsonToLucene({ query: text ?? '', filters }) || '*:*'
    if (query === '') {
      query = '*:*'
    }

    setUrlQuery({
      location,
      params: { search: text || null, filters },
      replaceParams: true
    })

    setQuery(query)
    setFilters(filters)

    if (!isInitialSearch) {
      setPage(1)
    }
    console.log({ sort })
    search({
      variables: {
        query,
        sort: [sort, '-updated'],
        limit
      }
    })
  }

  useEffect(() => {
    history.replace({
      ...history.location,
      state: { page }
    })
  }, [page, history])

  // show error toast
  useEffect(() => {
    if (error) {
      enqueueSnackbar(`Unable to search ${objectLabels.locations.plural}`, { variant: 'error' })
    }
  }, [error, enqueueSnackbar])

  const handleRequestSort = (ev: React.MouseEvent, name: string, direction: 'asc' | 'desc') => {
    const newSort = `${direction === 'desc' ? '-' : ''}${name}`

    setPage(1)
    setUrlQuery({ location, params: { sort: newSort } })
    search({
      variables: {
        query: query ?? '*:*',
        sort: [newSort, '-updated']
      }
    })
  }

  const { total_rows = 0, rows = [] } = data?.searchLocations ?? {}

  const onClick = useCallback((location) => {
    window.localStorage.setItem('masqueradeCompanyId', location._id)
    history.push('/dashboard')
  }, [])

  return (
    <BasePage>
      <DocumentTitle title={objectLabels.locations.plural} />
      <Grid container spacing={2}>
        <Grid item xs={9}>
          <Tabs
            value={tab ?? 'list'}
            classes={{ root: classes.tabs }}
            onChange={(ev, newTab) => {
              setTab(newTab)
              switch (newTab) {
                case 'map':
                  setShowMapView(true)
                  break
                case 'list':
                  setShowMapView(false)
                  break
                default:
                  setShowMapView(true)
              }
            }}
          >
            <Tab value="list" label="List" />
            <Tab value="map" label="Map" />
          </Tabs>
        </Grid>
        <Grid direction="row" justifyContent="flex-end" item xs={3} style={{ width: '100%', display: 'flex' }}>
          <NewLocationButton />
        </Grid>
      </Grid>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <LocationSearchBar
            loading={loading}
            filters={filters}
            onSearch={(query, filters, isInitialSearch) => {
              handleLocationSearch({ text: query, filters, isInitialSearch })
            }}
          />
        </Grid>
        <Grid item xs={12}>
          {!showMapView ? (
            <Card>
              <LocationSearchTable
                locations={loading ? [] : rows}
                loading={loading}
                rowsPerPage={limit}
                sort={sort}
                page={page}
                pages={Math.ceil(total_rows / limit)}
                onPageChange={(newPage) => setPage(newPage)}
                onRequestSort={handleRequestSort}
                onLocationClick={(location) => onClick(location)}
                onFetchNextPage={() => {
                  fetchMore({
                    variables: {
                      query: query ?? '*:*',
                      limit,
                      bookmark: data.searchLocations.bookmark
                    },
                    updateQuery(prev, { fetchMoreResult }) {
                      if (!fetchMoreResult) {
                        return prev
                      }

                      return {
                        ...prev,
                        searchLocations: {
                          ...prev.searchLocations,
                          bookmark: fetchMoreResult.searchLocations.bookmark,
                          rows: [...prev.searchLocations.rows, ...fetchMoreResult.searchLocations.rows]
                        }
                      }
                    }
                  })
                }}
              />
            </Card>
          ) : (
            <Card>
              <GoogleMapsComponent locations={rows} />
            </Card>
          )}
        </Grid>
      </Grid>
    </BasePage>
  )
}

export default Locations

interface Coordinate {
  lat: number
  lng: number
}

export function calculateCenterPoint(coordinates: Coordinate[]): Coordinate | null {
  if (coordinates.length === 0) {
    return null
  }

  let sumLat: number = 0
  let sumLng: number = 0

  for (const coord of coordinates) {
    sumLat += coord.lat
    sumLng += coord.lng
  }

  const centerLat: number = sumLat / coordinates.length
  const centerLng: number = sumLng / coordinates.length

  return { lat: centerLat, lng: centerLng }
}
